import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { faHistory } from '@fortawesome/pro-regular-svg-icons/faHistory'
import { toast } from 'react-toastify'
import { GET_ONE, UPDATE, useClient } from '@peracto/client'
import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'

import RuleForm from './RuleForm'
import RuleExceptions from './RuleExceptions'
import { ruleForApi, ruleForForm, ruleLabel, ruleSchema } from './ruleTypes'
import { showApiError } from './apiErrors'

const RuleEdit = () => {
    const { client } = useClient()
    const { ruleId } = useParams()
    const [rule, setRule] = useState(null)
    const [status, setStatus] = useState('loading')

    const fetchRule = useCallback(async () => {
        try {
            const { data } = await client(GET_ONE, 'tecdoc-brand-rules', {
                id: `tecdoc-brand-rules/${ruleId}`,
            })
            setRule(ruleForForm(data))
            setStatus('loaded')
        } catch (e) {
            setStatus(e.status === 403 ? 'unauthorised' : 'notFound')
        }
    }, [client, ruleId])

    useEffect(() => {
        fetchRule()
    }, [fetchRule])

    const onSubmit = async (values, actions) => {
        try {
            await client(UPDATE, 'tecdoc-brand-rules', {
                id: `tecdoc-brand-rules/${ruleId}`,
                data: ruleForApi(values),
            })
            toast.success('Brand rule saved.')
            actions.setSubmitting(false)
            fetchRule()
        } catch (e) {
            showApiError(e, actions)
        }
    }

    if (status === 'unauthorised') {
        return <UnauthorisedMessage />
    }

    if (status === 'notFound') {
        return (
            <NotFoundMessage
                url="/tecdoc-brand-rules"
                message="The brand rule you're looking for could not be found"
                buttonLabel="Go to Brand Rules"
            />
        )
    }

    return (
        <>
            <Helmet>
                <title>{rule ? rule.brandName : 'Brand Rule'} | TecDoc Brand Rules | Peracto</title>
            </Helmet>
            <div className="form-container">
                <Heading name={rule ? `Edit ${ruleLabel(rule)}` : 'Edit Brand Rule'}>
                    <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                        <Link className="btn btn-link mr-2" to={`/tecdoc-rule-changes?ruleId=${ruleId}`}>
                            <FontAwesomeIcon icon={faHistory} className="mr-2" />
                            Change History
                        </Link>
                        <Link className="btn btn-outline-primary" to="/tecdoc-brand-rules">
                            <FontAwesomeIcon icon={faArrowCircleLeft} className="mr-2" />
                            Back to Brand Rules
                        </Link>
                    </div>
                </Heading>
                {status === 'loading' ? (
                    <div className="card">
                        <div className="card-body">Loading...</div>
                    </div>
                ) : (
                    <>
                        <RuleForm modeEdit data={rule} onSubmit={onSubmit} schema={ruleSchema} />
                        <RuleExceptions rule={rule} exceptionsOnly={rule.ruleType === 'exception_list'} />
                    </>
                )}
            </div>
        </>
    )
}

export default RuleEdit
